import MenuIcon from '@mui/icons-material/Menu'
import { Box, Button, Stack, Paper, Typography } from '@mui/material'
import { ReactNode, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import Error from '~/components/error'
import UpgradeWrapper from '~/components/upgradeWrapper'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import useGetOrganization from '~/features/organizations/hooks/useGetOrganization'
import { TScenariosParams } from '~/features/scenarios/models'
import { pathForScenarioV2 } from '~/features/scenarios/utils'
import ScenariosSideNav from '~/layouts/account/scenariosLayout/scenariosSideNav'
import { EClientPath } from '~/models/api'
import { store } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function ScenariosLayout({ children }: { children: ReactNode | ReactNode[] }) {
  const [mobileSideNavOpen, setMobileSideNavOpen] = useState(false)

  const organizationId = store((state) => state.user.organizationId)
  const { isLoading, isError } = useGetOrganization(organizationId)

  const { isPlusUser } = useUserAccess()
  const { pathname } = useLocation()
  const { scenarioId = '' } = useParams<TScenariosParams>()

  const areNewScenariosPages = [
    EClientPath.manageScenariosV2,
    EClientPath.customScenarioV2Create,
    pathBuilder(EClientPath.scenarioActionV2CreateEdit, { actionId: '' }),
    pathForScenarioV2(scenarioId),
  ].some((path) => pathname.includes(path))

  if (!isPlusUser)
    return (
      <Paper
        sx={{
          paddingBlock: { xxxs: 1, sm: 2, md: 4 },
          paddingInline: { xxxs: 1, sm: 2, md: 4 },
        }}
      >
        <UpgradeWrapper shouldBelongToPlusPlan isMarkingPaidFeature>
          <Typography>
            You can now define simple to complex scenarios that your assistant
            can handle throughout the course of a conversation. The Scenario
            Builder allows you to define structured workflows that your AI
            assistant can follow, integrating seamlessly with your business
            systems to do things like book, cancel, and reschedule appointments,
            get customer info, and more. This capability is available for Plus
            subscribers.
          </Typography>
        </UpgradeWrapper>
      </Paper>
    )

  if (isError && !isLoading) {
    return <Error message="Something went wrong. Please try again later" />
  }

  return (
    <Box sx={{ position: 'relative', display: 'flex', height: '100%' }}>
      {!areNewScenariosPages && (
        <ScenariosSideNav
          mobileOpen={mobileSideNavOpen}
          setMobileOpen={setMobileSideNavOpen}
        />
      )}
      <Stack
        gap={2}
        sx={{
          overflow: 'auto',
          width: '100%',
          ...(areNewScenariosPages
            ? {}
            : {
                paddingBlock: { xxxs: rem(16), xs: rem(32), md: rem(64) },
                paddingInline: `clamp(${rem(24)},calc((100% - ${rem(1200)}) / 2),calc((100% - ${rem(1200)}) / 2))`,
              }),
        }}
      >
        {!areNewScenariosPages && (
          <Button
            onClick={() => setMobileSideNavOpen(!mobileSideNavOpen)}
            variant="secondary"
            startIcon={<MenuIcon />}
            sx={{ display: { md: 'none' } }}
          >
            Scenarios and Actions
          </Button>
        )}
        <Box sx={{ height: '100%' }}>{children}</Box>
      </Stack>
    </Box>
  )
}

export default ScenariosLayout
